/**
 * AccessAdmin — small internal panel for managing tool-access requests.
 *
 *  · Protected by an ADMIN_TOKEN entered once and stored in localStorage.
 *  · Lists every record in the `tool_access` collection.
 *  · One-click approve / revoke with optional notes.
 *  · Lives at /portal/access — out of the main nav, only the team finds it.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_ADMIN = "hv_admin_token";

const STATUS_COLOURS = {
  pending:  { bg: "rgba(194,146,46,0.10)", border: "rgba(194,146,46,0.40)", text: "#8A6914" },
  approved: { bg: "rgba(31,182,193,0.10)", border: "rgba(31,182,193,0.40)", text: "#0E7A82" },
  revoked:  { bg: "rgba(184,92,56,0.10)",  border: "rgba(184,92,56,0.40)",  text: "#B85C38" },
};

export default function AccessAdmin() {
  const [token, setToken] = useState(localStorage.getItem(STORAGE_ADMIN) || "");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("access");                  // "access" | "downloads"
  const [records, setRecords] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [pendingAction, setPendingAction] = useState(null);

  const load = useCallback(async (adminToken) => {
    setLoading(true);
    setErrorMsg("");
    try {
      // Fetch both tabs in parallel — small enough payloads.
      const [r1, r2] = await Promise.all([
        fetch(`${API}/api/access/admin/list`, { headers: { "Authorization": `Bearer ${adminToken}` } }),
        fetch(`${API}/api/access/admin/lead-magnets`, { headers: { "Authorization": `Bearer ${adminToken}` } }),
      ]);
      if (r1.status === 401 || r1.status === 403) {
        setAuthed(false);
        localStorage.removeItem(STORAGE_ADMIN);
        setErrorMsg("Admin token rejected. Try again.");
        return;
      }
      const d1 = await r1.json();
      const d2 = await r2.json();
      setRecords(d1.records || []);
      setDownloads(d2.records || []);
      setAuthed(true);
    } catch (e) {
      setErrorMsg("Could not reach the API.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_ADMIN);
    if (stored) load(stored);
  }, [load]);

  const signIn = async (e) => {
    e.preventDefault();
    if (!token) return;
    localStorage.setItem(STORAGE_ADMIN, token);
    await load(token);
  };

  const signOut = () => {
    localStorage.removeItem(STORAGE_ADMIN);
    setToken("");
    setAuthed(false);
    setRecords([]);
  };

  const act = async (email, action) => {
    setPendingAction(email + ":" + action);
    setErrorMsg("");
    try {
      const r = await fetch(`${API}/api/access/admin/${action}`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.detail || `Could not ${action}`);
      }
      await load(token);
    } catch (e) {
      setErrorMsg(e.message);
    } finally {
      setPendingAction(null);
    }
  };

  // -------------------- UI --------------------
  if (!authed) {
    // If we have a stored token, the load() in useEffect is in-flight —
    // render a tiny placeholder while it resolves. Only redirect to
    // /admin/login when there's no stored token AND no in-flight load.
    const stored = typeof window !== "undefined"
      ? localStorage.getItem(STORAGE_ADMIN) : null;
    if (!stored && typeof window !== "undefined"
        && window.location.pathname !== "/admin/login") {
      window.location.replace("/admin/login");
      return null;
    }
    return (
      <main className="min-h-screen flex items-center justify-center px-6 py-24"
            style={{ background: "#FAFAF7", color: "#0C2A40" }}
            data-testid="access-admin-loading">
        <div className="font-mono uppercase"
             style={{ fontSize: "0.65rem", letterSpacing: "0.22em",
                      color: "rgba(12,42,64,0.55)" }}>
          {errorMsg || "Signing in…"}
        </div>
      </main>
    );
  }

  return (
    <main
      className="min-h-screen px-6 py-12"
      style={{ background: "#FAFAF7", color: "#0C2A40" }}
      data-testid="access-admin-dashboard"
    >
      <Helmet>
        <title>Access admin — HidroVerse</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="font-mono uppercase" style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.6)" }}>
              HidroVerse · internal
            </div>
            <h1 style={{ fontSize: "1.6rem", fontWeight: 700, marginTop: 4 }}>
              Admin dashboard
            </h1>
          </div>
          <button
            type="button"
            onClick={signOut}
            data-testid="access-admin-signout"
            className="font-mono uppercase"
            style={{ fontSize: "0.62rem", letterSpacing: "0.18em", color: "rgba(12,42,64,0.65)", borderBottom: "1px dashed rgba(12,42,64,0.35)" }}
          >
            Sign out
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b mb-6 flex-wrap items-center" style={{ borderColor: "rgba(12,42,64,0.12)" }}>
          <TabButton active={tab === "access"} onClick={() => setTab("access")} count={records.length}
            testid="admin-tab-access">Tool-access requests</TabButton>
          <TabButton active={tab === "downloads"} onClick={() => setTab("downloads")} count={downloads.length}
            testid="admin-tab-downloads">Insight PDF downloads</TabButton>
          <Link
            to="/portal/admins"
            data-testid="admin-tab-admins"
            className="px-4 py-2.5 font-mono uppercase"
            style={{
              fontSize: "0.65rem",
              letterSpacing: "0.18em",
              fontWeight: 700,
              color: "rgba(12,42,64,0.60)",
              borderBottom: "2px solid transparent",
              textDecoration: "none",
              marginLeft: 4,
            }}
          >
            Admins →
          </Link>
          <Link
            to="/portal/content-planner"
            data-testid="admin-tab-content-planner"
            className="ml-auto inline-flex items-center gap-2 px-4 py-2 font-mono uppercase"
            style={{
              fontSize: "0.62rem",
              letterSpacing: "0.18em",
              fontWeight: 700,
              color: "#FFFFFF",
              background: "#0C2A40",
              border: "1px solid #0C2A40",
              textDecoration: "none",
              borderRadius: 999,
              marginBottom: 6,
            }}
          >
            Open Content Planner →
          </Link>
        </div>

        {errorMsg && (
          <div className="mb-4 px-4 py-3"
            style={{ background: "rgba(184,92,56,0.12)", border: "1px solid rgba(184,92,56,0.40)", color: "#B85C38", fontSize: "0.85rem" }}
            data-testid="access-admin-error"
          >
            {errorMsg}
          </div>
        )}

        {loading && (
          <p className="font-mono uppercase" style={{ fontSize: "0.65rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.55)" }}>
            Loading…
          </p>
        )}

        {!loading && tab === "access" && records.length === 0 && (
          <p className="font-light" style={{ fontSize: "0.95rem", color: "rgba(12,42,64,0.65)" }}>
            No tool-access requests yet.
          </p>
        )}

        {!loading && tab === "access" && records.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full" style={{ borderCollapse: "collapse", background: "#FFFFFF", border: "1px solid rgba(12,42,64,0.08)", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 2px rgba(12,42,64,0.04), 0 10px 28px -16px rgba(12,42,64,0.08)" }} data-testid="access-admin-table">
              <thead>
                <tr style={{ background: "#16181B", color: "#F5F7FA" }}>
                  <Th>Status</Th><Th>Email</Th><Th>Name</Th><Th>Company</Th><Th>Tool</Th><Th>Requested</Th><Th>Approved / revoked</Th><Th>Nudges</Th><Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => {
                  const c = STATUS_COLOURS[r.status] || STATUS_COLOURS.pending;
                  return (
                    <tr key={r.email} style={{ borderTop: "1px solid rgba(12,42,64,0.06)" }} data-testid={`access-row-${r.email}`}>
                      <Td>
                        <span
                          className="px-2 py-0.5 font-mono uppercase"
                          style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.text, fontSize: "0.6rem", letterSpacing: "0.18em", fontWeight: 700, borderRadius: 3 }}
                        >
                          {r.status}
                        </span>
                      </Td>
                      <Td><span style={{ fontFamily: "monospace", fontSize: "0.82rem" }}>{r.email}</span></Td>
                      <Td>{r.name || "—"}</Td>
                      <Td>{r.company || "—"}</Td>
                      <Td><span style={{ fontFamily: "monospace", fontSize: "0.78rem", color: "rgba(12,42,64,0.7)" }}>{r.tool || "—"}</span></Td>
                      <Td>{fmtDate(r.requested_at)}</Td>
                      <Td>{fmtDate(r.approved_at || r.revoked_at)}</Td>
                      <Td>{r.nudge_count || 0}</Td>
                      <Td>
                        <div className="flex gap-2 flex-wrap">
                          {r.status !== "approved" && (
                            <button
                              type="button"
                              onClick={() => act(r.email, "approve")}
                              disabled={pendingAction === `${r.email}:approve`}
                              data-testid={`access-approve-${r.email}`}
                              className="px-3 py-1 font-mono uppercase"
                              style={{ background: "#1FB6C1", color: "#0C2A40", fontSize: "0.62rem", letterSpacing: "0.15em", fontWeight: 700, border: "none", cursor: "pointer", opacity: pendingAction === `${r.email}:approve` ? 0.5 : 1 }}
                            >
                              {pendingAction === `${r.email}:approve` ? "…" : "Approve"}
                            </button>
                          )}
                          {r.status !== "revoked" && (
                            <button
                              type="button"
                              onClick={() => act(r.email, "revoke")}
                              disabled={pendingAction === `${r.email}:revoke`}
                              data-testid={`access-revoke-${r.email}`}
                              className="px-3 py-1 font-mono uppercase"
                              style={{ background: "transparent", color: "#B85C38", border: "1px solid rgba(184,92,56,0.40)", fontSize: "0.62rem", letterSpacing: "0.15em", fontWeight: 700, cursor: "pointer", opacity: pendingAction === `${r.email}:revoke` ? 0.5 : 1 }}
                            >
                              {pendingAction === `${r.email}:revoke` ? "…" : "Revoke"}
                            </button>
                          )}
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ─────────────────────────── DOWNLOADS TAB ─────────────────────────── */}
        {!loading && tab === "downloads" && downloads.length === 0 && (
          <p className="font-light" style={{ fontSize: "0.95rem", color: "rgba(12,42,64,0.65)" }}>
            No insight PDF downloads recorded yet.
          </p>
        )}

        {!loading && tab === "downloads" && downloads.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full" style={{ borderCollapse: "collapse", background: "#FFFFFF", border: "1px solid rgba(12,42,64,0.08)", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 2px rgba(12,42,64,0.04), 0 10px 28px -16px rgba(12,42,64,0.08)" }} data-testid="admin-downloads-table">
              <thead>
                <tr style={{ background: "#16181B", color: "#F5F7FA" }}>
                  <Th>When</Th><Th>Email</Th><Th>Name</Th><Th>Magnet</Th><Th>Source page</Th>
                </tr>
              </thead>
              <tbody>
                {downloads.map((d, i) => (
                  <tr key={`${d.email}-${d.created_at}-${i}`} style={{ borderTop: "1px solid rgba(12,42,64,0.06)" }}
                      data-testid={`download-row-${i}`}>
                    <Td>{fmtDate(d.created_at)}</Td>
                    <Td><span style={{ fontFamily: "monospace", fontSize: "0.82rem" }}>{d.email}</span></Td>
                    <Td>{d.name || "—"}</Td>
                    <Td>
                      <span style={{ fontFamily: "monospace", fontSize: "0.78rem", color: "rgba(12,42,64,0.7)" }}>
                        {d.magnet || "—"}
                      </span>
                    </Td>
                    <Td>
                      {d.source ? (
                        <a href={d.source} target="_blank" rel="noreferrer"
                           style={{ fontFamily: "monospace", fontSize: "0.78rem", color: "#1FB6C1", textDecoration: "underline" }}>
                          {d.source}
                        </a>
                      ) : "—"}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}

function TabButton({ active, onClick, children, count, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="px-5 py-3 font-mono uppercase transition-colors"
      style={{ fontSize: "0.65rem",
        letterSpacing: "0.2em",
        color: active ? "#0C2A40" : "rgba(12,42,64,0.55)",
        fontWeight: 700,
        background: "transparent",
        borderBottom: active ? "2px solid #1FB6C1" : "2px solid transparent",
        marginBottom: "-1px",
        cursor: "pointer", borderRadius: 999 }}
    >
      {children}
      <span className="ml-2" style={{
        background: active ? "rgba(31,182,193,0.20)" : "rgba(12,42,64,0.08)",
        color: active ? "#0E7A82" : "rgba(12,42,64,0.55)",
        padding: "1px 7px",
        borderRadius: 3,
        fontSize: "0.62rem",
      }}>
        {count}
      </span>
    </button>
  );
}

function Th({ children }) {
  return (
    <th className="px-3 py-2.5 text-left font-mono uppercase"
        style={{ fontSize: "0.6rem", letterSpacing: "0.18em", fontWeight: 700 }}>
      {children}
    </th>
  );
}
function Td({ children }) {
  return <td className="px-3 py-2.5" style={{ fontSize: "0.85rem", verticalAlign: "middle" }}>{children}</td>;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-ZA", { year: "numeric", month: "short", day: "numeric" }) + " · " + d.toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" });
  } catch (e) {
    return iso;
  }
}
